import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { LWApiPublicBillingBalanceResultRawI } from '@liquidweb-dev/club-sauce/public/billing/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import api from 'modules/queries/api';

const getAccountBalanceDisplay = ({
	due,
	balance,
}: {
	due: number;
	balance: number;
}) => {
	const dueLessBalance = due - balance;
	return dueLessBalance < 0 ? '0' : dueLessBalance.toFixed(2);
};

const getPastDue = ({
	overdue,
	balance,
}: {
	overdue: number;
	balance: number;
}) => {
	const pastDue = overdue - balance;
	return pastDue < 0 ? 0 : pastDue;
};

const getPendingCharges = ({
	due,
	overdue,
}: {
	due: number;
	overdue: number;
}) => {
	const pendingCharges = due - overdue;
	return pendingCharges < 0 ? 0 : pendingCharges;
};

export interface BillingBalance extends LWApiPublicBillingBalanceResultRawI {
	accountBalanceDisplay: string;
	pastDue: number;
	pendingCharges: number;
}

const supplementBillingBalance = (
	data: LWApiPublicBillingBalanceResultRawI,
): BillingBalance => {
	const { balance, due, overdue } = data;

	const accountBalanceDisplay = getAccountBalanceDisplay({
		due: Number(due),
		balance: Number(balance),
	});
	const pastDue = getPastDue({
		overdue: Number(overdue),
		balance: Number(balance),
	});
	const pendingCharges = getPendingCharges({
		due: Number(due),
		overdue: Number(overdue),
	});

	return {
		...data,
		accountBalanceDisplay,
		pastDue,
		pendingCharges,
	};
};

export const BILLING_BALANCE_QUERY_KEY = '/billing/balance';

const useBillingBalance = (
	options?: UseQueryOptions<
		LWApiPublicBillingBalanceResultRawI,
		ApiErrorI,
		BillingBalance
	>,
) =>
	useQuery({
		queryKey: [BILLING_BALANCE_QUERY_KEY],
		queryFn: () => api.billing.apiRaw.balance(),
		select: supplementBillingBalance,
		...options,
	});

export default useBillingBalance;
